import { calculatePercentDiscountCentavos } from "./money";

function normalizeCentavos(value) {
  return Math.max(0, Math.round(Number(value || 0)));
}

export function checkoutItemNeedsPriceOverrideReason(item, { planBound = false } = {}) {
  const it = item || {};
  if (planBound) return false;
  if (!it.procedure_id) return false;
  if (it.default_fee_centavos == null) return false;

  return Number(it.default_fee_centavos) !== normalizeCentavos(it.performed_price_centavos);
}

export function getCheckoutPriceOverrideLines(items, { isPlanBound = () => false } = {}) {
  return (Array.isArray(items) ? items : [])
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => checkoutItemNeedsPriceOverrideReason(item, { planBound: !!isPlanBound(item) }))
    .map(({ item, index }) => ({
      index,
      label: item?.procedure_name || item?.custom_name || "Procedure",
      default_fee_centavos: Number(item?.default_fee_centavos || 0),
      performed_price_centavos: normalizeCentavos(item?.performed_price_centavos),
      qty: Math.max(1, Number(item?.qty || 1)),
    }));
}

export function buildCheckoutItemPayloadRows({
  item,
  workKey = null,
  seniorPwdEligible = false,
  seniorPwdPercent = 0,
  patientDiscountType = null,
  priceOverrideReason = "",
  planBound = false,
  foldIntoPlan = false,
}) {
  const it = item || {};
  const outcome = it.outcome || "done";
  const qty = Math.max(1, Number(it.qty || 1));
  const unitPrice = normalizeCentavos(it.performed_price_centavos);
  const lineSubtotal = unitPrice * qty;
  const manualDiscount = normalizeCentavos(it.item_discount_centavos);
  const seniorPwdLine = (seniorPwdEligible && outcome === "done" && !planBound)
    ? calculatePercentDiscountCentavos(lineSubtotal, seniorPwdPercent)
    : 0;
  const totalDiscount = Math.min(lineSubtotal, manualDiscount + seniorPwdLine);
  const legalDiscountType = seniorPwdLine > 0 ? patientDiscountType : null;
  const manualDiscountType = manualDiscount > 0 ? "manual" : null;
  const discountEach = Math.floor(totalDiscount / qty);
  const discountRemainder = totalDiscount - discountEach * qty;
  const needsOverrideReason = checkoutItemNeedsPriceOverrideReason(it, { planBound });
  const hasManualDiscount = manualDiscount > 0;
  const adjustmentReason = it.price_override_reason?.trim()
    || priceOverrideReason?.trim()
    || undefined;
  const overrideReason = (needsOverrideReason || hasManualDiscount)
    ? adjustmentReason
    : undefined;

  return Array.from({ length: qty }, (_, index) => {
    const itemDiscountCentavos = discountEach + (index < discountRemainder ? 1 : 0);
    return {
      tn_id:                    index === 0 ? (it.tn_id ?? null) : null,
      procedure_visit_id:       index === 0 ? (it.procedure_visit_id ?? null) : null,
      treatment_plan_id:        it.treatment_plan_id ?? null,
      treatment_plan_item_id:   it.treatment_plan_item_id ?? null,
      work_key:                 index === 0 ? (workKey ?? null) : null,
      procedure_id:             it.procedure_id ?? null,
      custom_name:              it.custom_name ?? null,
      custom_category:          it.custom_category ?? null,
      source:                   it.source ?? null,
      payable_status:           it.payable_status ?? null,
      price_source:             it.price_source ?? null,
      ortho_case_id:            it.ortho_case_id ?? null,
      ortho_visit_type:         it.ortho_visit_type ?? null,
      procedure_instance_id:    it.procedure_instance_id ?? null,
      charge_event_id:          it.charge_event_id ?? null,
      tooth_number:             it.tooth_number || null,
      surface:                  it.surface || null,
      outcome,
      performed_price_centavos: unitPrice,
      item_discount_centavos:   itemDiscountCentavos,
      discount_type:            itemDiscountCentavos > 0 ? (legalDiscountType || manualDiscountType) : undefined,
      decline_reason:           outcome === "declined" ? (it.decline_reason || "Declined at checkout") : undefined,
      price_override_reason:    overrideReason,
      fold_into_plan:           foldIntoPlan || undefined,
    };
  });
}
